import React from 'react'
import { Modal, ModalHeader, ModalBody, Button, ModalFooter } from 'reactstrap'
import intl from 'react-intl-universal'
import { useAsync } from 'react-async'
import { useDispatch } from 'react-redux'
import { cloneDeep } from 'lodash'

import { PublishErrorProps, ButtonProps, JobType } from './types'
import {
  unscheduleUnpublish,
  unschedulePublish,
  updateDraftRevision,
} from '~/api/story'
import { AppError } from '~/api/AppError'
import { setStoryAns } from '~/redux/storyData'

interface Props extends PublishErrorProps {
  jobType?: string
  scheduledExpirationDate?: Date
  onClose: () => void
  fetchScheduledEvents: () => void
}

export default function UnscheduleModal({
  jobType = JobType.PUBLISH,
  scheduledExpirationDate,
  handleError,
  story,
  onClose,
  fetchScheduledEvents,
}: Props & ButtonProps) {
  const dispatch = useDispatch()

  const {
    run: runUnscheduleDocumentUnpublish,
    isLoading: isUnscheduleDocumentUnpublishLoading,
  } = useAsync({
    deferFn: async () => {
      const unscheduleUnpublishResult = await unscheduleUnpublish(story._id)
      return unscheduleUnpublishResult
    },
    onReject: (error: AppError) => {
      console.error('UnscheduleModal error', error)
      handleError(error.errorKey as string)
    },
    onResolve: () => {
      fetchScheduledEvents()
      onClose()
    },
  })

  const {
    run: runUnscheduleDocumentPublish,
    isLoading: isUnscheduleDocumentPublishLoading,
  } = useAsync({
    deferFn: async () => {
      const unschedulePublishResult = await unschedulePublish(story._id)
      return unschedulePublishResult
    },
    onReject: (error: AppError) => {
      console.error('UnscheduleModal error', error)
      handleError(error.errorKey as string)
    },
    onResolve: async () => {
      if (scheduledExpirationDate) {
        runUnscheduleDocumentUnpublish()
      } else {
        const updatedStory = cloneDeep(story)

        delete updatedStory.display_date

        if (!updatedStory.additional_properties?.publish_date) {
          // unscheduling-publish an unpublished story
          delete updatedStory.publish_date
        } else {
          // unscheduling-publish a previously published story
          updatedStory.publish_date = updatedStory.additional_properties
            .publish_date as string
        }

        await updateDraftRevision(story._id, updatedStory, dispatch)

        dispatch(
          setStoryAns({
            storyAns: updatedStory,
            // Omit isDirty set if action is unschedule
          })
        )
        fetchScheduledEvents()
        onClose()
      }
    },
  })

  const modalValues =
    jobType === JobType.PUBLISH
      ? {
          title: 'publishActions.unscheduleModal.unschedule',
          blurb: 'publishActions.unscheduleModal.blurb',
          button: 'publishActions.unscheduleModal.unscheduleButton',
        }
      : {
          title: 'publishActions.unscheduleExpirationModal.title',
          blurb: 'publishActions.unscheduleExpirationModal.blurb',
          button:
            'publishActions.unscheduleExpirationModal.cancelExpirationButton',
        }

  const isLoading =
    isUnscheduleDocumentPublishLoading || isUnscheduleDocumentUnpublishLoading

  return (
    <Modal isOpen toggle={onClose} className="bootstrap4">
      <ModalHeader toggle={onClose}>{intl.get(modalValues.title)}</ModalHeader>
      <ModalBody>{intl.get(modalValues.blurb)}</ModalBody>
      <ModalFooter>
        <Button outline={true} onClick={onClose}>
          {intl.get('publishActions.unscheduleModal.cancelButton')}
        </Button>
        <Button
          data-testid={`unschedule-${jobType}-modal`}
          disabled={isLoading}
          color="danger"
          onClick={() => {
            if (jobType === JobType.PUBLISH) {
              runUnscheduleDocumentPublish()
            } else {
              runUnscheduleDocumentUnpublish()
            }
          }}
        >
          {intl.get(modalValues.button)}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
